import React, { useState, useEffect } from 'react';
import { Scanner } from '@yudiel/react-qr-scanner';
import { X, Camera, ShieldAlert, RefreshCw } from 'lucide-react';

const QRScanner = ({ onScan, onClose }) => {
    const [cameraError, setCameraError] = useState(null);
    const [isSecure, setIsSecure] = useState(true);

    useEffect(() => {
        // Camera API requires HTTPS (except for localhost)
        if (!window.isSecureContext && window.location.hostname !== 'localhost') {
            setIsSecure(false);
        }
    }, []);

    const handleDecode = (text) => {
        if (text) {
            onScan(text);
        }
    };

    const handleError = (error) => {
        console.error("QR Scan Error Detail:", error);
        let errorMsg = error?.message || "Kamera başlatılamadı.";

        if (errorMsg.includes('NotAllowedError')) {
            errorMsg = "Kamera erişimine izin verilmedi. Lütfen tarayıcı ayarlarından izin verin.";
        } else if (errorMsg.includes('NotFoundError')) {
            errorMsg = "Cihazda uygun bir kamera bulunamadı.";
        } else if (errorMsg.includes('NotReadableError')) {
            errorMsg = "Kamera şu an başka bir uygulama tarafından kullanılıyor olabilir.";
        }

        setCameraError(errorMsg);
    };

    return (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                background: 'rgba(0,0,0,0.9)', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
                padding: '1rem', backdropFilter: 'blur(15px)'
            }}
        >
            <div
                onClick={e => e.stopPropagation()}
                style={{
                    background: 'var(--bg-card)', padding: '2rem',
                    borderRadius: '28px', width: '100%', maxWidth: '450px',
                    position: 'relative', border: '1px solid rgba(255,255,255,0.1)',
                    boxShadow: '0 25px 80px rgba(0,0,0,0.6)'
                }}
            >
                <button
                    onClick={onClose}
                    style={{
                        position: 'absolute', top: '1.25rem', right: '1.25rem',
                        background: 'var(--bg-main)', border: '1px solid var(--border)',
                        padding: '10px', borderRadius: '14px',
                        color: 'var(--text-muted)', cursor: 'pointer', zIndex: 10,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}
                >
                    <X size={20} />
                </button>

                <h3 style={{ marginBottom: '1.5rem', textAlign: 'center', fontWeight: 800, color: 'var(--text-main)', fontSize: '1.25rem' }}>Müşteri QR Tara</h3>

                <div style={{
                    width: '100%', aspectRatio: '1/1', borderRadius: '24px',
                    overflow: 'hidden', background: '#000',
                    position: 'relative', display: 'flex',
                    alignItems: 'center', justifyContent: 'center',
                    border: '2px solid var(--border)',
                    boxShadow: 'inset 0 0 40px rgba(0,0,0,0.5)'
                }}>
                    {!isSecure ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: '#f87171' }}>
                            <ShieldAlert size={48} style={{ margin: '0 auto 1.5rem', opacity: 0.8 }} />
                            <p style={{ fontWeight: 800, fontSize: '1.1rem' }}>HTTPS Gerekli</p>
                            <p style={{ fontSize: '0.875rem', marginTop: '0.75rem', color: 'var(--text-muted)' }}>
                                Kamera erişimi için güvenli bağlantı (SSL) gereklidir. Lütfen uygulamaya güvenli bir bağlantı üzerinden erişin.
                            </p>
                        </div>
                    ) : cameraError ? (
                        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                            <Camera size={48} style={{ margin: '0 auto 1.5rem', opacity: 0.3 }} />
                            <p style={{ fontWeight: 700, color: '#f87171' }}>{cameraError}</p>
                            <button
                                onClick={() => window.location.reload()}
                                style={{
                                    marginTop: '1.5rem', padding: '10px 20px', borderRadius: '10px',
                                    background: 'var(--primary)', color: 'white', border: 'none',
                                    fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center',
                                    gap: '8px', margin: '1.5rem auto 0'
                                }}
                            >
                                <RefreshCw size={16} /> Sayfayı Yenile
                            </button>
                        </div>
                    ) : (
                        <Scanner
                            onScan={(result) => handleDecode(result?.[0]?.rawValue)}
                            onError={handleError}
                            constraints={{
                                facingMode: 'environment'
                            }}
                            options={{
                                delayBetweenScanSuccess: 2000
                            }}
                            styles={{
                                container: { width: '100%', height: '100%' },
                                video: { objectFit: 'cover' }
                            }}
                            components={{
                                audio: true,
                                onOff: true,
                                tracker: true
                            }}
                        />
                    )}

                    {/* Scanning Frame Overlay */}
                    <div style={{
                        position: 'absolute', top: '10%', left: '10%', right: '10%', bottom: '10%',
                        border: '2px solid rgba(var(--primary-rgb), 0.3)', borderRadius: '30px',
                        pointerEvents: 'none', boxShadow: '0 0 0 2000px rgba(0,0,0,0.5)',
                        opacity: cameraError || !isSecure ? 0 : 1
                    }}>
                        <div style={{
                            position: 'absolute', top: -2, left: -2, width: 35, height: 35,
                            borderLeft: '5px solid var(--primary)', borderTop: '5px solid var(--primary)',
                            borderTopLeftRadius: '25px'
                        }} />
                        <div style={{
                            position: 'absolute', top: -2, right: -2, width: 35, height: 35,
                            borderRight: '5px solid var(--primary)', borderTop: '5px solid var(--primary)',
                            borderTopRightRadius: '25px'
                        }} />
                        <div style={{
                            position: 'absolute', bottom: -2, left: -2, width: 35, height: 35,
                            borderLeft: '5px solid var(--primary)', borderBottom: '5px solid var(--primary)',
                            borderBottomLeftRadius: '25px'
                        }} />
                        <div style={{
                            position: 'absolute', bottom: -2, right: -2, width: 35, height: 35,
                            borderRight: '5px solid var(--primary)', borderBottom: '5px solid var(--primary)',
                            borderBottomRightRadius: '25px'
                        }} />
                    </div>
                </div>

                <p style={{ marginTop: '1.75rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.5 }}>
                    Müşterinin telefonundaki QR kodu<br />kare içerisine gelene kadar yaklaştırın.
                </p>
            </div>
        </div>
    );
};

export default QRScanner;
